import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Breadcrumbs, Card, CardActionArea, CardContent, Typography, Grid2, Box,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import AppShell from './components/AppShell';
import MascotFoldedArms from './MascotFoldedArms';
import api from './services/api';

const icon = (Icon) => <Icon sx={{ fontSize: 36, color: 'primary.main' }} />;

const GROUPS = [
  {
    title: 'Quotas',
    columns: 4,
    actions: [
      {
        label: 'Alchemer quota setup',
        icon: icon(PlaylistAddRoundedIcon),
        path: 'add-quotas',
        hint: 'Create quotas in Alchemer for this survey.',
      },
      {
        label: 'Wrangler',
        icon: icon(HubRoundedIcon),
        path: 'wrangler',
        hint: "Set the survey up in Wrangler, see its status and jobs, and get the Alchemer webhook setup.",
      },
      {
        label: 'Monitoring',
        icon: icon(LeaderboardRoundedIcon),
        path: 'quota-monitoring',
        hint: 'Quota progress from Alchemer and Wrangler side by side, and quota views to share with partners.',
      },
      {
        label: 'Tracking',
        icon: icon(TimelineRoundedIcon),
        path: 'quota-tracker',
        hint: 'Track quota progress over time, for both systems.',
      },
    ],
  },
  {
    title: 'Survey tools',
    columns: 3,
    actions: [
      {
        label: 'Duplicate Removal',
        icon: icon(ContentCopyRoundedIcon),
        path: 'duplicate-removal',
        hint: 'Find and remove duplicate responses.',
      },
      {
        label: 'Open text analysis',
        icon: icon(FormatQuoteRoundedIcon),
        path: 'text-analysis',
        hint: 'Examine open text responses for AI and gibberish.',
      },
      {
        label: 'Disqualify IDs',
        icon: icon(CancelRoundedIcon),
        path: 'disqualify-ids',
        hint: "Disqualify a set of IDs you've identified elsewhere.",
      },
      {
        label: 'Survey Scripting',
        icon: icon(AutoFixHighRoundedIcon),
        path: 'scripting',
        hint: 'Upload a QNR document and auto-script questions into Alchemer.',
      },
      {
        label: 'Survey Testing',
        icon: icon(FactCheckRoundedIcon),
        path: 'survey-testing',
        hint: 'Check a scripted survey against its QNR and get a checklist of what to fix.',
      },
    ],
  },
];

const DEFAULT_MASCOT_TEXT = 'Survey loaded!\n\nChoose an action below...';

const SurveyDashboard = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mascotText, setMascotText] = useState(DEFAULT_MASCOT_TEXT);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => {
      setSurvey(res.data);
      setLoading(false);
    });
  }, [surveyId]);

  if (loading) return null;
  if (!survey) return <Typography sx={{ mt: 4, ml: 4 }}>Survey not found.</Typography>;

  const projectId = survey.project;

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${projectId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          Project
        </Link>
        <Typography color="textPrimary">{survey.name}</Typography>
      </Breadcrumbs>

      {/* Header with mascot */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, mb: 5 }}>
        <MascotFoldedArms text={mascotText} />
        <Box textAlign="center">
          <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
            {survey.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Alchemer ID: {survey.alchemer_survey_id}
          </Typography>
        </Box>
      </Box>

      {/* Action cards, in two groups */}
      {GROUPS.map((group) => (
        <Box key={group.title} sx={{ mb: 5 }}>
          <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={600} sx={{ mb: 2 }}>
            {group.title}
          </Typography>
          <Grid2 container spacing={3}>
            {group.actions.map(({ label, icon: actionIcon, path, hint }) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 12 / group.columns }} key={path}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 3,
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
                  }}
                >
                  <CardActionArea
                    component={Link}
                    to={`/survey/${surveyId}/${path}`}
                    onMouseEnter={() => setMascotText(hint)}
                    onMouseLeave={() => setMascotText(DEFAULT_MASCOT_TEXT)}
                    sx={{ p: 1 }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      {actionIcon}
                      <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600} sx={{ mt: 1 }}>
                        {label}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        </Box>
      ))}
    </AppShell>
  );
};

export default SurveyDashboard;
